import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {Button, CssVarsProvider, Radio, RadioGroup, Typography} from "@mui/joy";
import {createCustomTheme} from "./retheme";
import FormLabel from "@mui/joy/FormLabel";
import Sheet from "@mui/joy/Sheet";
import Box from "@mui/joy/Box";
import { db } from "../../utils/firebase";
import { ref, get, child, runTransaction } from "firebase/database";


const PollPage = () => {
    const { pollDataUrl } = useParams();
    const [pollData, setPollData] = useState(null);
    const [choice, setChoice] = useState(null);

    const [mode, setMode] = useState(null);
    const [questions, setQuestions] = useState({});
    const [indexNumber, setIndexNumber] = useState(0);

    const [canVoteCards, setCanVoteCards] = useState([]);
    const [allCards, setAllCards] = useState([]);

    const [currentCard, setCurrentCard] = useState(null);

    const [hasVoted, setHasVoted] = useState(false);

    const theme = pollData?.color?.trim() ? createCustomTheme("#" + pollData.color) : null;

    const [voteData, setVoteData] = useState({});

    useEffect(() => {
        const fetchPollData = async () => {
            try {
                const response = await fetch(decodeURIComponent(pollDataUrl));
                if (!response.ok) {
                    throw new Error(`Failed to fetch poll data: ${response.statusText}`);
                }
                const data = await response.json();
                setPollData(data);
                setQuestions(data.data.questions); // Update questions state
            } catch (error) {
                console.error('Error fetching poll data:', error);
            }
        };

        fetchPollData();
    }, [pollDataUrl]);

    useEffect(() => {
        const newCards = [];
        const newCanVoteCards = [];

        Object.keys(questions).forEach(key => {
            if (localStorage.getItem(`poll_voted_${pollDataUrl}_${key}`) === "true") {
                newCards.push(key);
            } else {
                newCanVoteCards.push(key);
                newCards.push(key);
            }
        });

        setCanVoteCards(newCanVoteCards);
        setAllCards(newCards);
        setIndexNumber(0);

        if (newCanVoteCards.length > 0) {
            setMode("poll");
            setCurrentCard(newCanVoteCards[0]);
            setHasVoted(false);
        } else {
            setMode("view");
            setCurrentCard(newCards[0]);
            setHasVoted(true);
            fetchVotes(); // 👈 Fetch votes from Firebase
        }
    }, [questions, pollDataUrl]);


    const fetchVotes = async () => {
        if (!pollData || !pollData.pollId) return;

        const pollID = pollData.pollId;
        const dbRef = ref(db);
        try {
            const snapshot = await get(child(dbRef, `polls/${pollID}`));
            if (snapshot.exists()) {
                setVoteData(snapshot.val());
            } else {
                console.log("No votes found for this poll.");
            }
        } catch (error) {
            console.error("Error fetching vote data:", error);
        }
    };


    const handleVote = async (selectedChoice, pollId) => {
        const votedKey = `poll_voted_${pollDataUrl}_${pollId}`;
        localStorage.setItem(votedKey, 'true');
        setChoice(selectedChoice);
        setHasVoted(true);

        const pollID = pollData.pollId;
        const voteRef = ref(db, `polls/${pollID}/${pollId}/${selectedChoice}`);

        try {
            await runTransaction(voteRef, (currentVotes) => {
                return (currentVotes || 0) + 1;
            });

            // 🟢 After successful vote update, fetch fresh data
            await fetchVotes();
        } catch (error) {
            console.error("Firebase vote update failed:", error);
        }
    };

    if (!pollData || !questions || !currentCard) {
        return <p>Loading poll...</p>;
    }

    const handleNextCard = () => {
        setChoice(null);
        setIndexNumber(prevIndex => {
            const newIndex = prevIndex + 1;

            if (mode === "poll") {
                if (newIndex < canVoteCards.length) {
                    setHasVoted(false);
                    setCurrentCard(canVoteCards[newIndex]);
                } else {
                    setMode("view");
                    setHasVoted(true);
                    setCurrentCard(allCards[0]); // reset to first view card
                    return 0; // reset index for view mode
                }
            } else if (mode === "view") {
                if (newIndex < allCards.length) {
                    setCurrentCard(allCards[newIndex]);
                } else {
                    setCurrentCard(allCards[0]);
                    // optionally loop or end
                    return 0; // keep at last
                }
            }

            return newIndex;
        });
    };

    const questionContents = questions[currentCard];

    const content = (
        <div className={"pdiv"}>
            <div className={"pdiv2"}>
                <Box sx={{width: '100%',}}>
                    {(questionContents.imageUrl && questionContents.imageUrl !== "") && (
                        <img src={questionContents.imageUrl} alt={questionContents.imageUrl} />
                    )}
                    <FormLabel
                        sx={{
                            mb: 2,
                            fontWeight: 'xl',
                            textTransform: 'uppercase',
                            fontSize: 'sm',
                            letterSpacing: '0.15rem',
                        }}
                    >
                        {questionContents.question}
                    </FormLabel>
                    {(questionContents.description && questionContents.description !== "") && (
                        <FormLabel
                            sx={{
                                mb: 2,
                                fontWeight: 'xl',
                                textTransform: 'uppercase',
                                fontSize: 'sm',
                                letterSpacing: '0.15rem',
                            }}
                        >
                            <Typography fontSize={"x-small"}>
                                {questionContents.description}
                            </Typography>
                        </FormLabel>
                    )}
                    <RadioGroup
                        aria-labelledby="storage-label"
                        size="lg"
                        sx={{ gap: 1.5 }}
                        onChange={(e) => {
                            if (!hasVoted) {
                                handleVote(e.target.value, currentCard);
                            }
                        }}
                        value={choice !== undefined ? choice : ""}
                    >
                        {Object.entries(questionContents.answers).map(([key, value]) => {
                            const questionVoteData = voteData[currentCard] || {};
                            const totalVotes = Object.values(questionVoteData).reduce((sum, count) => sum + count, 0);
                            const choiceVotes = questionVoteData[key] || 0;
                            const progress = totalVotes > 0 ? (choiceVotes / totalVotes) * 100 : 0;

                            return (
                                <Sheet key={key} sx={{ p: 2, borderRadius: 'md', boxShadow: 'sm' }}>
                                    <Radio
                                        label={value.text}
                                        overlay
                                        disableIcon
                                        disabled={hasVoted}
                                        value={key}
                                        slotProps={{
                                            label: ({ checked }) => ({
                                                sx: {
                                                    fontWeight: 'lg',
                                                    fontSize: 'md',
                                                    color: checked ? theme.vars.palette.primary[500] : theme.vars.palette.text.secondary,
                                                },
                                            }),
                                            action: ({ checked }) => ({
                                                sx: (theme) => ({
                                                    ...(checked && {
                                                        '--variant-borderWidth': '2px',
                                                        '&&': {
                                                            borderColor: theme.vars.palette.primary[500],
                                                        },
                                                    }),
                                                }),
                                            }),
                                        }}
                                    />
                                    {hasVoted && (
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                height: 10,
                                                backgroundColor: 'grey.300',
                                                borderRadius: 'sm',
                                                overflow: 'hidden',
                                                mt: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: `${progress}%`,
                                                    backgroundColor: 'primary.500',
                                                    transition: 'width 0.3s ease',
                                                }}
                                            />
                                        </Box>
                                    )}
                                    {hasVoted && (
                                        <Typography
                                            level="body-xs"
                                            sx={{ mt: 0.5, textAlign: 'right', color: 'text.secondary' }}
                                        >
                                            {choiceVotes} votes
                                        </Typography>
                                    )}
                                </Sheet>
                            );
                        })}
                    </RadioGroup>
                </Box>
                <br/>
                {hasVoted && (
                    <Button sx={{width: "100%"}} onClick={handleNextCard}>Next</Button>
                )}
                <div style={{display: "flex", flexDirection: "column", alignItems: "center", marginTop: "20px"}}>
                    <Typography style={{color: "#B3B3B3"}} fontSize={"10px"}>Powered By</Typography>
                    <a
                        href={"https://online-interactives.vercel.app/"}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{display: "flex", flexDirection: "column", alignItems: "center", marginTop: "3px"}}
                    >
                        <img width={"20%"} src={"/cdn/logo/logo-watermark.svg"} alt={""}/>
                    </a>
                </div>
            </div>
        </div>
    );

    return theme ? (
        <CssVarsProvider theme={theme}>
            {content}
        </CssVarsProvider>
    ) : (
        content
    );
};

export default PollPage;